import React from 'react';
import logger from '../lib/logger';

/**
 * Global ErrorBoundary — catches any uncaught render errors so a single
 * JavaScript defect does not blank the entire storefront for the user.
 *
 * Placed at the top of the tree (inside <BrowserRouter>). On error:
 *   - Log the full error + component stack to our logger (surfaces in server logs)
 *   - Fire an analytics `exception` event so GA4 exception tracking picks it up
 *   - Show a graceful fallback with a reload button + contact email
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    try {
      logger.error('[ErrorBoundary] uncaught render error', error, info?.componentStack);
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'exception', {
          description: String(error?.message || error).slice(0, 250),
          fatal: true,
        });
      }
    } catch { /* never let error reporting throw */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6" data-testid="error-boundary-fallback">
        <div className="max-w-md text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0l-7.07 12A2 2 0 004.93 19z"/>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-slate-600 mb-6">
            We hit an unexpected error. Refresh the page or head back to home — most things should work fine.
            If this keeps happening, email <a className="text-teal-700 underline" href="mailto:hello@goodhabitsfamily.com">hello@goodhabitsfamily.com</a>.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleReload}
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-3 rounded-full transition"
              data-testid="error-boundary-reload"
            >
              Refresh page
            </button>
            <a
              href="/"
              className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 font-semibold px-5 py-3 rounded-full transition"
              data-testid="error-boundary-home"
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
